'use client'

import { ReactElement, useContext } from 'react'
import { ConfigContext } from '../../../context/ConfigContext'
import Accordion from '../../Accordion/Accordion'
import InputCheckbox from '../../Form/InputCheckbox'
import { Switch } from '../../Switch/Switch'
import FilterProductFormPriceController from '../FilterProductFormPriceController'
import { ProductFilterFormContext } from '../FilterUtils/ProductFilterFormContext'
import useCurrentLocale from '../../../../core/hook/useCurrentLocale'
import { getIsArabicLocale } from '../../../utils/locale.utils'
import { AggregationsFilters } from '../../../../domain/cmsCategory'

export type FilterProductsProps = {
  aggregations: AggregationsFilters
  colorFilters: ColorFilter[]
  categoriesFilters: CategoriesFilters[]
  collectionFilters: CollectionProductFilter[]
  allPrices: number[]
}

const getTitleOfItemAccordion = (title: string, selectedFiels: number[]) => {
  if (!selectedFiels.length) {
    return title
  }

  return `${title} (${selectedFiels.length})`
}

type item = {
  title: string | ReactElement
  content: ReactElement
}
type items = item[]

const FilterProducts = ({
  aggregations,
  colorFilters,
  categoriesFilters,
  collectionFilters,
  allPrices,
}: FilterProductsProps) => {
  const { storeConfig } = useContext(ConfigContext)
  const { formState, onChangeFilter, onChangePriceFilter, onChangeAvailableOnline } =
    useContext(ProductFilterFormContext)

  const minNumber = Math.min(...allPrices)
  const maxNumber = Math.max(...allPrices)

  const renderAccordionItems = () => {
    const itemsArray: items = []
    const currentLocale = useCurrentLocale()
    const isArabicLocale = getIsArabicLocale(currentLocale)

    const category: item = {
      title: getTitleOfItemAccordion(aggregations?.type as string, formState.types),
      content: (
        <>
          {categoriesFilters.map((categorieFilter) => {
            const label = isArabicLocale
              ? `(${categorieFilter.numberOfOccurences}) ${categorieFilter.label}`
              : `${categorieFilter.label} (${categorieFilter.numberOfOccurences})`

            return (
              <InputCheckbox
                key={categorieFilter.id}
                id={categorieFilter.id.toString()}
                className='filter-product-form-fields-item'
                label={label}
                value={categorieFilter.id}
                checked={formState.types.includes(categorieFilter.id)}
                onChange={() => {
                  onChangeFilter('types', categorieFilter.id)
                }}
              />
            )
          })}
        </>
      ),
    }
    const collection: item = {
      title: getTitleOfItemAccordion(
        aggregations?.collection_product as string,
        formState.collections,
      ),
      content: (
        <>
          {collectionFilters.map((collectionFilter) => {
            const label = isArabicLocale
              ? `(${collectionFilter.numberOfOccurences}) ${collectionFilter.label}`
              : `${collectionFilter.label} (${collectionFilter.numberOfOccurences})`

            return (
              <InputCheckbox
                key={collectionFilter.id}
                id={collectionFilter.id.toString()}
                className='filter-product-form-fields-item'
                label={label}
                value={collectionFilter.id}
                checked={formState.collections.includes(collectionFilter.id)}
                onChange={() => onChangeFilter('collections', collectionFilter.id)}
              />
            )
          })}
        </>
      ),
    }
    const color: item = {
      title: getTitleOfItemAccordion(aggregations?.couleur_metal as string, formState.colors),
      content: (
        <>
          {colorFilters.map((colorFilter) => {
            const label = isArabicLocale
              ? `(${colorFilter.numberOfOccurences}) ${colorFilter.label}`
              : `${colorFilter.label} (${colorFilter.numberOfOccurences})`

            return (
              <InputCheckbox
                key={colorFilter.id}
                id={colorFilter.id.toString()}
                className='filter-product-form-fields-item'
                label={label}
                value={colorFilter.id}
                checked={formState.colors.includes(colorFilter.id)}
                onChange={() => {
                  onChangeFilter('colors', colorFilter.id)
                }}
              />
            )
          })}
        </>
      ),
    }
    const price: item = {
      title: aggregations?.price as string,
      content: (
        <FilterProductFormPriceController
          minRange={minNumber}
          maxRange={maxNumber}
          initialMinValue={formState.priceRange.min}
          initialMaxValue={formState.priceRange.max}
          onChange={onChangePriceFilter}
          isArabicLocale={isArabicLocale}
        />
      ),
    }

    if (collectionFilters.length !== 0 && categoriesFilters.length !== 0) {
      itemsArray.push(category)
      itemsArray.push(collection)
    }
    if (collectionFilters.length === 0) {
      itemsArray.push(category)
    }
    if (categoriesFilters.length === 0) {
      itemsArray.push(collection)
    }
    itemsArray.push(color)
    if (storeConfig.display_prices === 1) {
      itemsArray.push(price)
    }

    return itemsArray
  }

  return (
    <div className='filterproducts'>
      <div className='filterproducts-categories'>
        {storeConfig.ecommerce === 1 && (
    
          <div className='filterproducts-availableonline'>
            <p>{aggregations?.en_vente}</p>
            <Switch
              label=''
              color='white'
              value={formState.availableOnline}
              onChange={onChangeAvailableOnline}
            />
          </div>
          )}
        <Accordion items={renderAccordionItems()} product={null} />
      </div>
    </div>
  )
}

export default FilterProducts
